import { queryOptions } from "@tanstack/react-query";
import {
  getAnalysis,
  getAnalysisInfo,
  getRecentAnalyses,
  getUserAnalyses,
  getFavoriteAnalyses,
  searchAnalyses,
  getAvailableObjectTypes,
  getStats,
} from "./api";
import { computeAnalysisAttributes } from "./analyses";

export const analysisKeys = {
  all: ["analyses"] as const,
  report: (id: string) => [...analysisKeys.all, "report", id] as const,
  info: (id: string) => [...analysisKeys.all, "info", id] as const,
  recent: (limit: number) => [...analysisKeys.all, "recent", limit] as const,
  user: (limit: number) => [...analysisKeys.all, "user", limit] as const,
  favorites: () => [...analysisKeys.all, "favorites"] as const,
  search: (query: string, types: string[]) =>
    [...analysisKeys.all, "search", query, [...types].sort()] as const,
};

export const objectTypeKeys = {
  all: ["objectTypes"] as const,
};

export const statsKeys = {
  all: ["stats"] as const,
};

export const analysisReportOptions = (analysisId: string) =>
  queryOptions({
    queryKey: analysisKeys.report(analysisId),
    queryFn: async () => {
      const { report: jsonData } = await getAnalysis({ analysisId });
      if (!jsonData?.SummaryDescription?.RatingFlag) {
        throw new Error("Invalid JSON: Missing required fields");
      }
      return jsonData;
    },
  });

export const analysisInfoOptions = (analysisId: string) =>
  queryOptions({
    queryKey: analysisKeys.info(analysisId),
    queryFn: () => getAnalysisInfo({ analysisId }),
    staleTime: 0,
  });

export const recentAnalysesOptions = (limit: number = 10) =>
  queryOptions({
    queryKey: analysisKeys.recent(limit),
    queryFn: () => getRecentAnalyses({ limit }),
    select: (data) => data.analyses.map(computeAnalysisAttributes),
    staleTime: 0,
  });

export const userAnalysesOptions = (limit: number = 100) =>
  queryOptions({
    queryKey: analysisKeys.user(limit),
    queryFn: () => getUserAnalyses({ limit }),
    select: (data) => data.analyses.map(computeAnalysisAttributes),
    staleTime: 0,
  });

export const favoriteAnalysesOptions = () =>
  queryOptions({
    queryKey: analysisKeys.favorites(),
    queryFn: () => getFavoriteAnalyses(),
    select: (data) => data.analyses.map(computeAnalysisAttributes),
    staleTime: 0,
  });

export const searchAnalysesOptions = (query: string, types: string[]) =>
  queryOptions({
    queryKey: analysisKeys.search(query, types),
    queryFn: () => searchAnalyses({ limit: 100, query, types }),
    select: (data) => data.analyses.map(computeAnalysisAttributes),
    staleTime: 0,
  });

export const availableObjectTypesOptions = () =>
  queryOptions({
    queryKey: objectTypeKeys.all,
    queryFn: () => getAvailableObjectTypes(),
    select: (data) => data.availableTypes,
    staleTime: 0,
  });

export const statsOptions = () =>
  queryOptions({
    queryKey: statsKeys.all,
    queryFn: () => getStats(),
    staleTime: 0,
  });
